import { FormikProps } from "formik";
import React, { useMemo, useRef } from "react";
import Button from "../../Button";
import DatePicker from "../../DatePicker";
import Input from "../../Input";
import { MainFormType } from "../CreateServiceModal";

interface PropTypes {
  form: FormikProps<MainFormType>;
  onSubmit: () => void;
  isLoading?: boolean
}

const OrderInformation = ({ form, onSubmit, isLoading }: PropTypes) => {
  const orderInfo = form.values.curbside_pickup_meta.orderInformation;

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isBusinessUser = form.values.user?.selectedAccountType === "business";

  const errors = useMemo(() => {
    if (!form.errors.curbside_pickup_meta?.orderInformation) return {};
    return form?.errors?.curbside_pickup_meta?.orderInformation;
  }, [form.errors.curbside_pickup_meta?.orderInformation]);

  const touched = form.touched.curbside_pickup_meta?.orderInformation || {};

  // Helper function to check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };

  // Calculate minTime based on selected date
  const getMinTime = () => {
    const selectedDate = form.values.scheduleTime;
    const today = new Date();

    if (!selectedDate || isSameDay(selectedDate, today)) {
      // If no date selected or today is selected, use current time as minimum
      return new Date();
    } else {
      // If future date is selected, allow selection from start of day (00:00)
      return new Date(new Date().setHours(0, 0, 0, 0));
    }
  };

  // Handle date/time change with validation
  const handleDateTimeChange = (date: Date | null) => {
    if (!date) {
      form.setFieldValue("scheduleTime", null);
      return;
    }

    const today = new Date();
    const minTime = isSameDay(date, today)
      ? new Date()
      : new Date(new Date().setHours(0, 0, 0, 0));

    // If switching to today and current selected time is before minTime, update to minTime
    if (isSameDay(date, today) && date < minTime) {
      // Set the date to current time (which is the minimum allowed)
      form.setFieldValue("scheduleTime", new Date());
    } else {
      form.setFieldValue("scheduleTime", date);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    form.setFieldValue(`curbside_pickup_meta.orderInformation.${field}`, value);
  };

  const handleFieldBlur = (field: string) => {
    form.setFieldTouched(
      `curbside_pickup_meta.orderInformation.${field}`,
      true
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <p className="text-lg font-semibold text-center">Order information</p>
      <div className="md:grid gap-4 md:grid-cols-2 flex flex-col">
        <Input
          onChange={(e) => handleFieldChange("orderNumber", e.target.value)}
          error={errors.orderNumber}
          onBlur={() => handleFieldBlur("orderNumber")}
          value={orderInfo.orderNumber}
          autoFocus
          name="orderNumber"
          isInvalid={Boolean(touched.orderNumber && errors.orderNumber)}
          label="Order number* "
          placeholder="Enter order number"
        />
        <Input
          onChange={(e) => handleFieldChange("storeName", e.target.value)}
          error={errors.storeName}
          onBlur={() => handleFieldBlur("storeName")}
          value={orderInfo.storeName}
          name="storeName"
          isInvalid={Boolean(touched.storeName && errors.storeName)}
          label="Store name *"
          placeholder="Enter store name"
        />

        <div className="">
          <div className="label">
            <span className="label-text text-[1rem] md:text-xs">Proof of purchase*</span>
          </div>

          <input
            type="file"
            accept="image/*"
            ref={(ref) => (fileInputRef.current = ref)}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                form.setFieldValue(
                  "curbside_pickup_meta.orderInformation.proofOfPurchase",
                  e.target.files[0]
                );
              }
            }}
          />

          <button
            type="button"
            className="text-[1rem] md:text-xs px-4 w-full h-10 md:h-[2.6rem] flex items-center border border-primary rounded-lg py-2 md:flex-nowrap flex-wrap"
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="shrink-0">Selected file:&nbsp;</span>
            <span className="break-all md:truncate">
              {form.values.curbside_pickup_meta.orderInformation.proofOfPurchase instanceof File
                ? form.values.curbside_pickup_meta.orderInformation.proofOfPurchase.name
                : ""}
            </span>
          </button>
          {errors.proofOfPurchase ? (
            <div className="label">
              <span className="label-text-alt text-red-600">
                {errors.proofOfPurchase}
              </span>
            </div>
          ) : null}
        </div>
        <Input
          onChange={(e) => handleFieldChange("storeLink", e.target.value)}
          error={errors.storeLink}
          onBlur={() => handleFieldBlur("storeLink")}
          value={orderInfo.storeLink}
          name="storeLink"
          isInvalid={Boolean(touched.storeLink && errors.storeLink)}
          label="Store link *"
          placeholder="Enter store link"
        />
        {isBusinessUser && (
          <DatePicker
            showTimeSelect
            minDate={new Date()} // Disables past dates
            minTime={getMinTime()} // Conditionally set minimum time
            maxTime={new Date(new Date().setHours(23, 59, 59, 999))} // Set to end of day
            placeholderText="Pickup now"
            selected={form.values.scheduleTime}
            label="Schedule time"
            popperPlacement="top"
            dateFormat="dd/MM/yyyy HH:mm"
            className='text-[1rem] lg:text-xs'
            inputClassName='text-[1rem] lg:text-xs'
            onChange={handleDateTimeChange}
          />
        )}
      </div>
      <div className="col-start-1 col-end-3 flex justify-center mt-3 md:mr-3">
        <Button isLoading={isLoading} className="w-full md:w-32 min-h-0 h-10 md:h-10" type="submit">Continue</Button>
      </div>
    </form>
  );
};

export default OrderInformation;
